import type { IReportTypeDef } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Center, Loader } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import { fetchReportTypes, type IReportTypeOverride, updateReportTypes } from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import type { IReportTypeRow } from "./interfaces"
import { ReportTypesPanelView } from "./ReportTypesPanelView"

/** Resolved type → editable row (display fields + the four tunable fields). */
function toRow(type: IReportTypeDef): IReportTypeRow {
  return {
    id: type.id,
    label: type.label,
    audience: type.audience,
    isBuiltin: type.isBuiltin,
    enabled: type.enabled,
    fitPrompt: type.fitPrompt,
    generationPrompt: type.generationPrompt,
    threshold: type.threshold,
  }
}

/**
 * Diff edited rows against the loaded baseline into the minimal per-org override
 * batch. Only changed types ride along. `enabled` + `threshold` are always
 * pinned on a changed row (both are definite values, so this avoids the upsert's
 * "omitted → default" trap). Prompts are sent ONLY when edited, so an untouched
 * builtin prompt is never copied into the override — a cleared prompt sends `""`,
 * which WS0's `inheritStr` resolves back to the builtin.
 */
function buildOverrides(rows: IReportTypeRow[], baseline: IReportTypeRow[]): IReportTypeOverride[] {
  const baseById = new Map(baseline.map((row) => [row.id, row]))
  const overrides: IReportTypeOverride[] = []
  for (const row of rows) {
    const base = baseById.get(row.id)
    if (!base) continue
    const fitChanged = row.fitPrompt !== base.fitPrompt
    const genChanged = row.generationPrompt !== base.generationPrompt
    const changed = row.enabled !== base.enabled || row.threshold !== base.threshold || fitChanged || genChanged
    if (!changed) continue
    const override: IReportTypeOverride = { typeId: row.id, enabled: row.enabled, threshold: row.threshold }
    if (fitChanged) override.fitPrompt = row.fitPrompt
    if (genChanged) override.generationPrompt = row.generationPrompt
    overrides.push(override)
  }
  return overrides
}

/**
 * Smart wrapper for the Settings → Report Types admin panel. Loads the resolved
 * per-company report-type registry, holds the editable rows, and saves the diff
 * back as a batch of per-org overrides. The visual surface lives in the dumb
 * {@link ReportTypesPanelView}.
 */
export function ReportTypesPanel(): React.JSX.Element {
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const { i18n } = useLingui()
  const queryClient = useQueryClient()

  const { data, isLoading } = useCompanyQuery({
    key: queryKeys.reportTypes,
    queryFn: (company) => fetchReportTypes(company),
  })

  const baseline = useMemo(() => (data ? data.types.map(toRow) : []), [data])
  const [rows, setRows] = useState<IReportTypeRow[] | null>(null)
  // The workspace Heavy-model gate, seeded alongside the rows. WHICH model the
  // Heavy tier uses now lives in Settings → Model selection (the `heavy_model`
  // key), so this panel only owns the on/off gate, not the model choice.
  const [bestModelEnabled, setBestModelEnabled] = useState<boolean | null>(null)
  // Set on a successful save so the next refetch RE-SEEDS the editable rows from
  // the server's resolved truth. This matters because the GET returns RESOLVED
  // types: clearing a prompt to inherit the builtin comes back as the builtin
  // text (not ""), so adopting the submitted rows would leave the panel falsely
  // dirty. A save always changes the resolved set (dirty gates it), so the query
  // data ref always changes → this flag is always consumed on the next fetch.
  const justSavedRef = useRef(false)

  // Seed the editable state on first load, and re-seed after a save completes.
  useEffect(() => {
    if (!data) return
    if (rows === null || justSavedRef.current) {
      setRows(data.types.map(toRow))
      setBestModelEnabled(data.bestModelEnabled)
      justSavedRef.current = false
    }
  }, [data, rows])

  const rowsDirty = rows !== null && JSON.stringify(rows) !== JSON.stringify(baseline)
  const bestModelDirty = data != null && bestModelEnabled !== null && bestModelEnabled !== data.bestModelEnabled
  const dirty = rowsDirty || bestModelDirty

  const { mutate, isPending } = useCompanyMutation({
    mutationFn: (company) =>
      updateReportTypes(company, buildOverrides(rows ?? [], baseline), {
        bestModelEnabled: bestModelDirty ? (bestModelEnabled ?? undefined) : undefined,
      }),
    onSuccess: () => {
      notifications.show({ message: i18n._(msg`Report types saved.`), color: "success" })
      // Adopt the refetched resolved set (see justSavedRef) so `dirty` clears.
      justSavedRef.current = true
      queryClient.invalidateQueries({ queryKey: queryKeys.reportTypes(orgId) })
    },
    onError: () =>
      notifications.show({ message: i18n._(msg`Couldn't save report types. Try again.`), color: "danger" }),
  })

  if (isLoading || rows === null) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    )
  }

  return (
    <ReportTypesPanelView
      rows={rows}
      saving={isPending}
      dirty={dirty}
      bestModelEnabled={bestModelEnabled ?? true}
      onRowsChange={setRows}
      onBestModelChange={setBestModelEnabled}
      onSave={() => mutate()}
      onReset={() => {
        setRows(baseline)
        setBestModelEnabled(data?.bestModelEnabled ?? true)
      }}
    />
  )
}
